/**
 * Sites BOQ "Auto-book stock" (19 #7): the server previews which open BOQ lines free stock can book —
 * earliest project end date first — and what stays short; one click books it all (and, ticked, sends the
 * shortage to Procurement as purchase requests). Nothing changes until "Book …" is pressed, and the server
 * refuses (409) if stock moved since the preview — the panel then shows the new preview.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, CircleAlert, Loader2, RefreshCw, WandSparkles } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { notify } from '@/components/ui/notify';
import { getErrorMessage } from '@/lib/errors';
import { formatDate, formatNumber } from '@/lib/format';
import { InvCheckbox } from './InventoryUi';
import { formatQty, formatUnitTotals, locationShort, plural } from './inventoryModel';

/** Pieces and metres are never added together: '6,642 pcs · 12 m' (older servers: one number in pcs). */
const unitTotals = (byUnit, qty) => (byUnit && Object.keys(byUnit).length ? formatUnitTotals(byUnit) : `${formatQty(qty)} pcs`);

/** "MTN693003 → LIV Waterside 4 · Luxor Tower 4 · from AD Store" — the first booked parts, one line each. */
function bookingLines(bookings = [], limit = 2) {
  const byPart = new Map();
  bookings.forEach((booking) => {
    if (!byPart.has(booking.part_number)) byPart.set(booking.part_number, { projects: new Map(), stores: new Set() });
    const entry = byPart.get(booking.part_number);
    entry.projects.set(booking.project_name, (entry.projects.get(booking.project_name) || 0) + booking.qty);
    entry.stores.add(booking.location);
  });
  return [...byPart.entries()].slice(0, limit).map(([part, entry]) => ({
    part,
    text: `${[...entry.projects.entries()].map(([name, qty]) => `${name} ${formatQty(qty)}`).join(' · ')} · from ${[...entry.stores].map(locationShort).join(', ')}`
  }));
}

export default function AutoBookPanel({ projectId = null, projectName = '', canSendShortage = false, onApplied, onClose }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [sendShortage, setSendShortage] = useState(canSendShortage);
  const [busy, setBusy] = useState(false);
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/inventory/auto-book', { project_id: projectId || '', dry_run: true });
      if (requestId === requestRef.current) setPreview(res.data || null);
    } catch (err) {
      if (requestId === requestRef.current) { setPreview(null); setError(getErrorMessage(err, 'Could not prepare the preview')); }
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { setNotice(''); load(); }, [load]);

  // "Lines" as Sites BOQ counts them: one per part, however many projects need it.
  const toBook = preview?.booking_parts || 0;
  // Purchase requests are one per project line — the unit the server creates and the toast reports.
  const toSend = sendShortage && canSendShortage ? preview?.request_lines || 0 : 0;
  const nothing = !toBook && !toSend;

  const apply = async () => {
    if (!preview?.plan_key || nothing) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const res = await api.post('/inventory/auto-book', {
        project_id: projectId || '',
        dry_run: false,
        plan_key: preview.plan_key,
        send_shortage: Boolean(toSend)
      });
      const data = res.data || {};
      notify.success(
        `Booked ${plural(data.booking_parts ?? toBook, 'line')} from stock${data.created_requests ? ` · sent ${plural(data.created_requests, 'purchase request')} to Procurement` : ''}`,
        { description: `${projectName || 'All projects'}${data.created_requests ? ` · needed by ${formatDate(data.needed_by)}` : ''}` }
      );
      onApplied?.();
      onClose?.();
    } catch (err) {
      if (err?.response?.status === 409) {
        setNotice('Stock or bookings changed a moment ago — this is the new preview. Check it and book again.');
        load();
      } else {
        setError(getErrorMessage(err, 'Could not book the stock'));
      }
    } finally {
      setBusy(false);
    }
  };

  const examples = bookingLines(preview?.bookings);
  const buttonLabel = toBook && toSend
    ? `Book ${plural(toBook, 'line')} & send ${plural(toSend, 'request')}`
    : toBook ? `Book ${plural(toBook, 'line')}` : toSend ? `Send ${plural(toSend, 'request')}` : 'Nothing to book';

  return (
    <div className="border-t border-line-2 px-5 py-3.5" data-testid="auto-book-panel" aria-live="polite">
      <p className="text-[12.5px] text-ink-3">
        <b className="font-bold text-ink">Auto-book preview</b>
        {' '}· {projectName ? <>for <b className="font-semibold text-ink-2">{projectName}</b> · </> : null}books free stock against open BOQ lines, earliest project end date first · nothing changes until you confirm
      </p>
      {loading ? (
        <p className="mt-2.5 flex items-center gap-2 text-[13px] text-ink-3"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Matching free stock to open BOQ lines…</p>
      ) : preview ? (
        <div className="mt-2.5 flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex min-w-[240px] flex-1 items-start gap-2.5" data-testid="auto-book-bookings">
            <span className="ui-icon-tile tone-green mt-0.5 h-7 w-7 rounded-lg" aria-hidden="true"><CheckCircle2 className="h-4 w-4" /></span>
            <div className="min-w-0">
              <p className="text-[13.5px] text-ink">
                <b className="font-bold">{plural(toBook, 'line')} booked from stock</b>
                <span className="text-ink-2"> · {unitTotals(preview.booking_by_unit, preview.booking_qty)}</span>
              </p>
              {examples.length ? examples.map((example) => (
                <p key={example.part} className="truncate text-[12px] text-ink-3" title={example.text}>
                  <span className="font-mono text-[11.5px] text-ink-2">{example.part}</span> → {example.text}
                </p>
              )) : <p className="text-[12px] text-ink-3">No free stock matches an open BOQ line.</p>}
              {preview.booking_parts > examples.length ? <p className="text-[11.5px] text-ink-3">+ {plural(preview.booking_parts - examples.length, 'more line')} · {plural(preview.booking_lines, 'booking')} in all</p> : null}
            </div>
          </div>
          <div className="flex min-w-[240px] flex-1 items-start gap-2.5" data-testid="auto-book-shortage">
            <span className="ui-icon-tile tone-red mt-0.5 h-7 w-7 rounded-lg" aria-hidden="true"><CircleAlert className="h-4 w-4" /></span>
            <div className="min-w-0">
              <p className="text-[13.5px] text-ink">
                <b className="font-bold text-status-red-ink">{plural(preview.short_parts ?? preview.short_lines, 'line')} short</b>
                <span className="text-ink-2" data-testid="auto-book-short-qty"> · {unitTotals(preview.short_by_unit, preview.short_qty)} to buy</span>
              </p>
              {canSendShortage ? (
                <label className="mt-0.5 flex cursor-pointer items-start gap-2 text-[12.5px] text-ink-2">
                  <InvCheckbox className="mt-px" checked={sendShortage} onCheckedChange={(checked) => setSendShortage(Boolean(checked))} data-testid="auto-book-send-shortage" aria-label="Send to Procurement as purchase requests" />
                  <span>
                    Send to Procurement as purchase requests
                    <span className="text-ink-3"> · {plural(preview.request_lines, 'request')}, one per project{preview.already_requested_lines ? ` · ${formatNumber(preview.already_requested_lines)} already requested` : ''}</span>
                  </span>
                </label>
              ) : (
                <p className="text-[12px] text-ink-3">Sending the shortage to Procurement needs Material access.</p>
              )}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="button" onClick={apply} disabled={busy || nothing} data-testid="auto-book-apply">
              <WandSparkles aria-hidden="true" /> {busy ? 'Booking…' : buttonLabel}
            </Button>
          </div>
        </div>
      ) : null}
      {notice ? <p className="mt-2 text-[12.5px] font-semibold text-status-amber-ink" role="status" data-testid="auto-book-notice">{notice}</p> : null}
      {error ? (
        <p className="mt-2 flex items-center gap-2 text-[12.5px] font-semibold text-status-red-ink" role="alert">
          {error}
          <Button type="button" size="xs" variant="outline" className="text-ink" onClick={load}><RefreshCw aria-hidden="true" /> Try again</Button>
        </p>
      ) : null}
    </div>
  );
}
